import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { allArticles } from '../Data/articles';
import { ProjectsData } from '../Data/projects';
import { ChoiceGroup } from '../../design/ui';
import { ArrowRight } from '../../design/icons';
import PathOrb from './PathOrb';
import './content-paths.css';

// Curated connections, with writing and project titles read from their registries.
// Lab links describe the experiment; projects are related work, not dependencies.
const PATHS = [
    { value: 'retrieve', label: 'Retrieve & rank', article: 'how-rag-retrieval-actually-works',
        lab: 'rag-pipeline', experiment: 'Query a live RAG pipeline', project: 'hybrid-rag',
        orb: 'searching', scope: 'Context', source: 'A question', target: 'Ranked context',
        title: 'Context, not guesswork.',
        description: 'Find the useful pieces. Rank them. Inspect what actually reaches the model.' },
    { value: 'learn', label: 'Learn the model', article: 'backprop-forge-transformer-in-wasm',
        lab: 'backprop-forge', experiment: 'Train the transformer in your browser', project: 'llm-finetuning',
        orb: 'solving', scope: 'Gradients', source: 'Tokens', target: 'Updated weights',
        title: 'Follow the gradient.',
        description: 'Look inside a transformer. Trace its backward pass, then train it in your browser.' },
    { value: 'device', label: 'Run on device', article: 'ml-formats-and-runtimes',
        lab: 'format-tree', experiment: 'Explore model formats and runtimes', project: null,
        orb: 'shaping', scope: 'Runtimes', source: 'A model', target: 'A runtime',
        title: 'Closer to the machine.',
        description: 'Follow the formats and runtime choices that take a model from training to a device.' },
    { value: 'agents', label: 'Route the work', article: 'agentic-ai-salesforce',
        lab: 'the-gate', experiment: 'Try the language and relevance gate', project: 'n8n',
        orb: 'connecting', scope: 'Guardrails', source: 'A request', target: 'A checked route',
        title: 'Autonomy needs a system.',
        description: 'Follow the checks, tools, and boundaries around an agent. Try the gate before a request reaches the model.' },
] as const;

type PathId = typeof PATHS[number]['value'];

export default function ContentPaths() {
    const [path, setPath] = useState<PathId>(PATHS[0].value);
    const current = PATHS.find(item => item.value === path)!;
    const article = allArticles.find(item => item.slug === current.article);
    const project = ProjectsData.find(item => item.id === current.project);
    const links = [
        { action: 'Read the reasoning', title: article?.title ?? 'Browse the working notes', href: article ? `/article/${article.slug}` : '/articles' },
        { action: 'Run the experiment', title: current.experiment, href: `/labs/${current.lab}` },
        ...(current.value === 'device' ? [{ action: 'Try a mechanical experiment', title: 'Reach & constraints', href: '/labs/inverse-kinematics' }] : []),
        ...(project ? [{ action: 'Related project', title: project.title, href: `/about?project=${encodeURIComponent(project.id)}` }] : []),
    ];

    return (
        <section className="content-paths" aria-labelledby="content-paths-title">
            <div className="content-paths__explorer">
                <div className="content-paths__intro">
                    <div className="content-paths__heading">
                        <h2 id="content-paths-title">Follow <em>an idea.</em></h2>
                        <p>Choose a system. Follow it from a working note to a browser experiment, then into the work around it.</p>
                    </div>
                    <ChoiceGroup<PathId> label="Explore a system" value={path} onChange={setPath}
                        options={PATHS.map(item => ({
                            value: item.value,
                            label: <><span>{item.label}</span><span className="content-paths__scope">{item.scope}</span><ArrowRight size={16} /></>,
                        }))} />
                </div>
                <figure className="content-paths__instrument">
                    <PathOrb state={current.orb} source={current.source} target={current.target} />
                    <figcaption className="content-paths__summary">
                        <h3>{current.title}</h3>
                        <p>{current.description}</p>
                        <span>Concept preview, not live agent activity.</span>
                    </figcaption>
                </figure>
            </div>
            <div className="content-paths__links" aria-live="polite">
                {links.map(link => (
                    <Link key={link.action} to={link.href}>
                        <span className="content-paths__action">{link.action}</span>
                        <strong>{link.title}</strong><ArrowRight size={18} />
                    </Link>
                ))}
            </div>
        </section>
    );
}
